// src/features/planning/pages/WorkCenterCapacityProfileListPage.tsx
// ADR-098 Rule CAP-FE-02/03. الگوی دقیقاً هم‌سطح صفحات Reference Entity
// ساده (بدون Family/Revision) — مشابه ساختار MasterScheduleListPage
// (ADR-092) از نظر layout/glass-panel/btn-primary، ولی چون این یک Config
// Entity ساده است (نه Family)، فرم افزودن به‌صورت این‌لاین در همین صفحه
// است، نه یک صفحهٔ Create جدا.
//
// ⚠️ نکات نیازمند تأیید قبل از merge (ADR-098 Action Item #1):
// 1. نام هوک‌های زیر (useV1PlanningCapacityProfilesList/Create) بر اساس
//    الگوی useV1Planning<PathSegment><Action> در صفحات MPS موجود حدس زده
//    شده‌اند (خروجی مسیر روتر "capacity-profiles") — بعد از
//    npm run api:generate با v1.ts واقعی تطبیق دهید.
// 2. فیلد work_center به‌صورت select ساده روی یک هوک فرضی
//    useV1ProductionRoutingWorkCentersList نوشته شده — من در این session
//    مسیر واقعی API دامنهٔ routing/WorkCenter را نخوانده‌ام، پس این نام
//    را هم verify کنید. اگر چنین هوکی وجود نداشت، فیلد را به یک <input>
//    متنی برای UUID برگردانید (fallback امن‌تر، پایین کامنت شده).

import { useState } from "react";
import {
  useV1PlanningCapacityProfilesList,
  useV1PlanningCapacityProfilesCreate,
} from "@/api/generated/v1/v1";
import { useV1ProductionWorkCentersList } from "@/api/generated/v1/v1";
import { usePermission } from "@/auth/usePermission";
import { extractResults } from "@/shared/utils/apiResponseUtils";
import { PageBanner } from "@/shared/components/PageBanner";
import type { WorkCenterCapacityProfile } from "../types";

export function WorkCenterCapacityProfileListPage() {
  const canManage = usePermission("planning.manage");

  const { data, isLoading, refetch } = useV1PlanningCapacityProfilesList();
  const profiles: WorkCenterCapacityProfile[] = extractResults<WorkCenterCapacityProfile>(data);

  const { data: wcData } = useV1ProductionWorkCentersList();
  const workCenters = extractResults<{ id: string; code: string; name: string }>(wcData);

  const { mutateAsync: createProfile, isPending } = useV1PlanningCapacityProfilesCreate();

  const [workCenterId, setWorkCenterId] = useState("");
  const [availableMinutes, setAvailableMinutes] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!workCenterId || !availableMinutes) {
      setError("شناسهٔ Work Center و ظرفیت (دقیقه) الزامی هستند.");
      return;
    }
    try {
      await createProfile({
        data: {
          work_center: workCenterId,
          available_minutes_per_period: Number(availableMinutes),
        } as any,
      });
      setWorkCenterId("");
      setAvailableMinutes("");
      refetch();
    } catch {
      setError("ثبت پروفایل ظرفیت با خطا مواجه شد. شناسهٔ Work Center را بررسی کنید.");
    }
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="پروفایل‌های ظرفیت مراکز کاری"
        subtitle="تعریف ظرفیت در دسترس (دقیقه در هر بازه) برای ماشین‌آلات و ایستگاه‌های کاری"
        icon="🏭"
        helpLink="/help/planning/work-center-capacity-profiles"
      />

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

        {canManage && (
          <form onSubmit={handleCreate} className="mb-6 flex gap-3 items-end flex-wrap">
            <label className="flex flex-col text-sm text-muted">
              Work Center
              <select
                className="input-field min-w-[200px]"
                value={workCenterId}
                onChange={(e) => setWorkCenterId(e.target.value)}
              >
                <option value="">-- انتخاب کنید --</option>
                {workCenters.map((wc) => (
                  <option key={wc.id} value={wc.id}>
                    [{wc.code}] {wc.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-sm text-muted">
              ظرفیت در بازه (دقیقه)
              <input
                className="input-field"
                type="number"
                min={1}
                value={availableMinutes}
                onChange={(e) => setAvailableMinutes(e.target.value)}
              />
            </label>
            <button type="submit" className="btn-primary" disabled={isPending}>
              {isPending ? "در حال ثبت..." : "+ افزودن پروفایل"}
            </button>
          </form>
        )}

        {error && <p style={{ color: "#ef4444" }}>{error}</p>}

        {isLoading ? (
          <p className="text-muted">در حال بارگذاری...</p>
        ) : profiles.length === 0 ? (
          <p className="text-muted text-center p-6">هیچ پروفایل ظرفیتی هنوز تعریف نشده.</p>
        ) : (
          <table className="w-full text-sm text-left rtl:text-right border-collapse">
            <thead>
              <tr>
                <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">Work Center</th>
                <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">ظرفیت (دقیقه در بازه)</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-3 py-2.5 font-mono text-xs">{p.work_center}</td>
                  <td className="px-3 py-2.5">{p.available_minutes_per_period}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
